import React, { useEffect, useState } from "react";
import CustomDataGrid from "../../components/dms_common/CustomDataGrid";
import "./DeliveryTable.css";
import { DownloadSingleReportConfig } from "../../types/types";
import { useNavigate } from "react-router-dom";
import { DeliveryInvoiceDetailType } from "../../types/types";
import { reInitialiseCollectionData, setCollectionDisableEditing, setDeliveryStatus, setReturnDetails } from "../../stateManagement/actions/collectionActions";
import { GridRenderCellParams } from "@mui/x-data-grid";
import RenderSvg from "../../components/renderSvg/RenderSvg";
import { getSalesReturnDetails } from "./DeliveryConfirmationService";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { ChannelkartNetworkPut } from "../../services/networkService";
// Define the status mapping
const invoiceDeliveryStatusMapping = [
  {
    id: "INVOICED",
    dmsLabelColor: "#CC911F",
    dmsLabel: "Pending",
    dmsColor: "#FFF9EF",
  },
  {
    id: "DELIVERED",
    dmsLabelColor: "#029847",
    dmsLabel: "Delivered",
    dmsColor: "#EAFFF4",
  },
  {
    id: "CANCELLED",
    dmsLabelColor: "#C44C36",
    dmsLabel: "Cancelled",
    dmsColor: "#FFEFF0",
  },
  {
    id: "PARTIAL DELIVERED",
    dmsLabelColor: "#17B4A3",
    dmsLabel: "Partially Delivered",
    dmsColor: "#F0FBFF",
  },
];

const DeliveryTable = (props:{data:DeliveryInvoiceDetailType[], refreshData:()=>void}) => {
  const [data, setData] = useState<DeliveryInvoiceDetailType[]>([]);
  const [downloadSingleReportConfig, setDownloadSingleReportConfig] = useState<DownloadSingleReportConfig | null>();
  const navigate = useNavigate(); // For navigation
  const [checkedIdArray, setCheckedIdArray] = useState<string[]>([]);
  const [selectableRowIds, setSelectableRowIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const columnsMetaData = {
    columns: [
      { headerName: "Invoice No.",
        headerParts:{top: "Invoice", bottom: "No."},
        mappedValue: "invoiceNumber", 
        width: 12  },
      { headerName: "Delivery Date",
        headerParts:{top: "Delivery", bottom: "Date"},
        mappedValue: "deliveryDate",
        width: 12 ,},
      {
        headerName: "Outlet Name & Code",
        headerParts:{top: "Outlet Name", bottom: " & Code"},
        width: 15,
        mappedValue: "",
        cellRenderElement: (params: GridRenderCellParams) => (
          <div style={{display:"flex", flexDirection:"column"}}>
            <div style={{overflow: "hidden",textOverflow: "ellipsis", width: "9rem", whiteSpace: "nowrap"}}>
              {params?.row?.outletName}
            </div>
            <div style={{display:"flex", gap:"6px"}}>
              <div>{params?.row?.outletCode}</div>
              {params?.row?.gstApplicable && <div style={{height:"12px",marginTop:"1px"}}><RenderSvg name='gstIcon' /></div>}
            </div>
          </div>
        )
      },
      { headerName: "SKUs", 
        mappedValue: "skuCount", 
        width: 5, 
      },
      { headerName: "Amount",
         mappedValue: "amount", 
         width: 8,
         currencySymbol: "₹", },
      { headerName: "Tax Amount",
        headerParts:{top: "Tax", bottom: "Amount"}, 
        mappedValue: "taxAmount", 
        includeCurrenySymbol: true,
        width: 8,
        currencySymbol: "₹", },
      { headerName: "Discount Amount", 
        headerParts:{top: "Discount", bottom: "Amount"},
        mappedValue: "discountAmount",
         width: 8,
         currencySymbol: "₹", },
      { headerName: "Final Amount", 
        headerParts:{top: "Final", bottom: "Amount"},
        mappedValue: "finalAmount", 
        width: 8,
        currencySymbol: "₹", },
      { headerName: "Status",
        mappedValue: "status", 
        width: 10},
    ],
    Actions: ["redirect", "download"],
  };

   const handleRedirect = async (redirectUrl: string, rowData: DeliveryInvoiceDetailType) => {
    reInitialiseCollectionData();
    delete localStorage.deliveryCollectionData;
    const { returnDetailsArray, returnDetailsObject, returnSalesDetails } = await getSalesReturnDetails(rowData.invoiceNumber);
    if(returnDetailsObject){
      setReturnDetails(returnDetailsObject);
    }
    if(rowData.status.toLowerCase() != "invoiced"){
      setCollectionDisableEditing(true);
    }
    setDeliveryStatus(rowData.status);
    navigate(redirectUrl, {
      state: {
        invoiceDetails: rowData, // Pass the rowData as invoiceDetails
        returnDetailsArray: returnDetailsArray
      },
    });
    };

  const fetchData = async () => {
    try {
      const response = props.data;
      const formattedData = response.map((row) => ({
        ...row,
        id: row.invoiceNumber, // Add unique `id` field
      }));
      setData(formattedData);
      const selectableIds = formattedData.filter(data=>data.status.toLowerCase() == "invoiced" ).map(data=>data.id);
      setSelectableRowIds(selectableIds);
    } catch (error) {
      console.error("Error fetching delivery data:", error);
    }
  };

  const validateResponse = (response : any, invoiceType: string): string =>{
    
    const failedInvoices = response.data
      .filter((invoice: any) => invoice.individualStatus !== "SUCCESS")
      .map((invoice: any) => invoice.invoiceNumber);

    if (failedInvoices.length > 0) {
      if (failedInvoices.length === response.data.length && invoiceType.toLowerCase() === "delivered") {
        return "Failed to mark the selected invoices as Delivered. Please try again!";
      }
      return "Unable to mark these invoices as " + invoiceType.toLowerCase() + ": " + failedInvoices.join(", ");
    }
    return "";
  };

  const deliverSelectedInvoices = async() => {
    setIsSubmitting(true);
    try{
      const user = JSON.parse(localStorage.authContext).user;
      const deliveredInvoices = checkedIdArray.map((invoiceNumber) => {
        const invoiceRow = data.find((row) => row.invoiceNumber === invoiceNumber);
        return {
          referenceNumber: invoiceNumber,
          supplier: user.loginId,
          loginId: user.loginId,
          outletCode: invoiceRow ? invoiceRow.outletCode : "",
          status: "DELIVERED"
        };
      });
      const requestBody = { deliveredInvoices };
      const response = await ChannelkartNetworkPut("/v1/sales/delivered", requestBody);
      const errorMessage = validateResponse(response, "delivered");
      if(errorMessage) {
        openPopup("Error",errorMessage);
      } else{
        openPopup("Success","Selected invoices marked as delivered");
      }
    }catch(e){
      console.log(e);
    }
    finally{
      props.refreshData();
      setIsSubmitting(false);
    }
  };

  const cancelSelectedInvoices = async () => {
    setIsSubmitting(true);
    try {
      const user = JSON.parse(localStorage.authContext).user;
      const cancelledInvoices = checkedIdArray.map((invoiceNumber) => {
        const invoiceRow = data.find((row) => row.invoiceNumber === invoiceNumber);
        return {
          referenceNumber: invoiceNumber,
          supplier: user.loginId,
          loginId: user.loginId,
          outletCode: invoiceRow ? invoiceRow.outletCode : "",
          status: "CANCELLED"
        };
      });
      const requestBody = { cancelledInvoices };
      const response = await ChannelkartNetworkPut("/v1/sales/cancelled", requestBody);
      const errorMessage = validateResponse(response, "cancelled");
      if (errorMessage) {
        openPopup("Error", errorMessage);
      } else {
        openPopup("Success", "Selected invoices marked as cancelled");
      }
    } catch (e) {
      console.error(e);
    } finally {
      props.refreshData();
      setIsSubmitting(false);
    }
  };
  
  const DeliverySubmitFooter = ()=>{
    return (
      <div className="delivery-multi-select-submit-button-container">
        <button
          className={`delivery-multi-select-submit ${checkedIdArray.length==0 ||isSubmitting ? 'disabled' : ''}`}
          onClick={deliverSelectedInvoices}
          >
          <span className="delivery-multi-select-submit-text">
              Mark as delivered
          </span>
        </button>
      </div>
    );
  }

  useEffect(() => {
    fetchData();
  }, [props.data]);

  const updateCheckedIdArray = (selectedIds: any[])=>{
    const invariantIds = checkedIdArray.filter(checkedId=>!selectableRowIds.includes(checkedId));
    const updatedCheckedIdArray = Array.from(new Set([...invariantIds, ...selectedIds]))
    setCheckedIdArray(updatedCheckedIdArray);
  }

  return (
      <CustomDataGrid
        rows = {Array.isArray(data) ? data : []}
        columnsMetaData={columnsMetaData}
        tableWidth={0.7}
        redirectUrl="invoiceDetails"
        redirectUrlFunction= {handleRedirect}
        orderStatusConfig={invoiceDeliveryStatusMapping}
        downloadSingleReportConfig={downloadSingleReportConfig}
        isCheckboxSelection = {true}
        footerElement={DeliverySubmitFooter}
        checkedIdArray={checkedIdArray}
        setCheckedIdArray={updateCheckedIdArray}
        selectableRowIds = {selectableRowIds}
      />
  );
};


export default DeliveryTable;